/**
 * Invoice Modal Component
 * Modal for creating a Fortnox invoice from a booking
 */

import { useState, useId } from "react";
import type { Booking } from "@common/types";
import { useTranslation } from "react-i18next";
import BookingDialog from "./BookingDialog";
import { useLocaleStore } from "../stores/locale-store";
import { formatBookingDate } from "../utils/booking-format";

interface InvoiceModalProps {
  booking: Booking;
  onClose: () => void;
  onCreateInvoice: (invoiceDate: string) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

export default function InvoiceModal({
  booking,
  onClose,
  onCreateInvoice,
  isLoading = false,
  error = null,
}: InvoiceModalProps) {
  const { t } = useTranslation();
  const language = useLocaleStore(state => state.language);
  const titleId = useId();
  const dateId = useId();
  // Pre-fill invoice date with trip date
  const [invoiceDate, setInvoiceDate] = useState<string>(booking.date);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try { await onCreateInvoice(invoiceDate); } catch { /* Error is supplied by the owning mutation. */ }
  };

  // Calculate totals for preview
  const tripTotal = booking.tripPrice || 0;
  const lunchTotal = booking.totalLunchPrice || 0;
  const extraCost = booking.extraCost || 0;
  const articlesTotal = (booking.articles || []).reduce(
    (sum, article) => sum + article.quantity * article.unitPrice,
    0,
  );
  const grandTotal = tripTotal + lunchTotal + extraCost + articlesTotal;

  return (
    <BookingDialog titleId={titleId} onClose={onClose} busy={isLoading}>
      <div className="min-h-0 overflow-y-auto p-4 sm:p-6 [overflow-wrap:anywhere]">
        <h3 id={titleId} className="text-lg font-semibold text-sea-900 mb-4">
          {t("partnerPages.invoice.title")}
        </h3>

        <p className="text-sm text-gray-500 mb-4">
          {t("partnerPages.invoice.booking")} <span className="font-mono">{booking.confirmationCode}</span>
        </p>

        {/* Invoice Preview */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            {t("partnerPages.invoice.preview")}
          </h4>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">{t("partnerPages.invoice.trip")} {booking.tripName}</span>
              <span className="font-medium">
                {tripTotal} {booking.tripCurrency}
              </span>
            </div>

            {booking.includeLunch && lunchTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">
                  {t("partnerPages.invoice.lunch")} ({booking.numberOfGuests} × {booking.lunchPricePerGuest}
                  )
                </span>
                <span className="font-medium">
                  {lunchTotal} {booking.tripCurrency}
                </span>
              </div>
            )}

            {extraCost > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">
                  {t("partnerPages.invoice.extra")} {booking.extraCostDescription || t("partnerPages.invoice.additional")}
                </span>
                <span className="font-medium">
                  {extraCost} {booking.tripCurrency}
                </span>
              </div>
            )}

            {/* Extra Articles */}
            {booking.articles && booking.articles.length > 0 && (
              <div className="border-t border-gray-200 pt-2 mt-2">
                <div className="text-xs font-medium text-gray-500 mb-1">
                  {t("partnerPages.invoice.articles")}
                </div>
                {booking.articles.map((article, index) => (
                  <div
                    key={index}
                    className="flex justify-between text-gray-600"
                  >
                    <span>
                      {article.description} ({article.quantity} ×{" "}
                      {article.unitPrice})
                    </span>
                    <span className="font-medium">
                      {article.quantity * article.unitPrice}{" "}
                      {booking.tripCurrency}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t border-gray-200 pt-2 flex justify-between font-semibold">
              <span>{t("partnerPages.invoice.total")}</span>
              <span className="text-sea-700">
                {grandTotal} {booking.tripCurrency}
              </span>
            </div>
          </div>

          {/* References */}
          <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
            <div className="flex justify-between">
              <span>{t("partnerPages.invoice.ourReference")}</span>
              <span className="font-mono">{booking.confirmationCode}</span>
            </div>
            {booking.bookingReference && (
              <div className="flex justify-between mt-1">
                <span>{t("partnerPages.invoice.yourReference")}</span>
                <span className="font-mono">{booking.bookingReference}</span>
              </div>
            )}
            <div className="flex justify-between mt-1">
              <span>{t("partnerPages.invoice.customer")}</span>
              <span>{booking.partnerName}</span>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor={dateId} className="label">{t("partnerPages.invoice.date")}</label>
            <input
              id={dateId}
              type="date"
              className="input"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {t("partnerPages.invoice.prefilled", { date: formatBookingDate(booking.date, language) })}
            </p>
          </div>

          {error && (
            <div role="alert" className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isLoading}
            >
              {t("partnerPages.edit.cancel")}
            </button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t("partnerPages.invoice.creating")}
                </span>
              ) : (
                t("partnerPages.invoice.create")
              )}
            </button>
          </div>
        </form>
      </div>
    </BookingDialog>
  );
}
